import React, { useState, useEffect } from 'react';
import { db, functions, auth } from '../firebase/config';
import { getDoc, doc, addDoc, collection, serverTimestamp, query, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Search, CornerUpLeft, CornerUpRight, Archive, Trash2 } from 'lucide-react';

import CreateLabelModal from '../components/crm/CreateLabelModal';
import ComposeEmailModal from '../components/crm/ComposeEmailModal';
import EmailSidebar from '../components/crm/EmailSidebar';
import EmailConnectScreen from '../components/crm/EmailConnectScreen';
import EmailContentDisplay from '../components/crm/EmailContentDisplay';
import { decodeBase64Url, decodeHtmlEntities } from '../utils/decoders';

const EmailView = ({
    user,
    composeState,
    onComposeRequest,
    onComposeClose,
    isConnected,
    onConnect,
    accessToken,
    tokenExpiresAt,
    emailProvider,
    onProviderChange,
    // mailtok-2c7f9e51 — the old `onConnectMicrosoft` prop is gone. CrmApp no longer passes one:
    // its Firebase-popup version stored a Firebase Auth refresh token as a Microsoft token and was
    // deleted. This view's own handleConnectMicrosoft (the server-side redirect flow) is the path.
}) => {
    const [labels, setLabels] = useState([])
    const [error, setError] = useState('')
    const [emails, setEmails] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const [selectedEmail, setSelectedEmail] = useState(null)
    const [activeFolder, setActiveFolder] = useState(emailProvider === 'microsoft' ? 'inbox' : 'INBOX')
    const [showCreate, setShowCreate] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [customers, setCustomers] = useState([]); 
    
    const currentAccessToken = accessToken;
    const currentTokenExpiresAt = tokenExpiresAt;
    
    const [isComposeOpen, setIsComposeOpen] = useState(composeState.isOpen);
    useEffect(() => {
        setIsComposeOpen(composeState.isOpen)
    }, [composeState.isOpen])

    useEffect(() => {
        if (!user?.uid || !emailProvider) return; 

        const userDocRef = doc(db, 'users', user.uid);
        getDoc(userDocRef).then(userDoc => {
            if (userDoc.exists()) {
                const orgId = userDoc.data().organizationId;
                if (orgId) {
                    const customersPath = `organizations/${orgId}/customers`;
                    const qCustomers = query(collection(db, customersPath));
                    
                    const unsubscribeCustomers = onSnapshot(qCustomers, (snapshot) => {
                        setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    });
                    
                    return () => unsubscribeCustomers();
                }
            }
        }).catch(error => {
            console.error("Error fetching user doc for orgId:", error);
        });
        
    }, [user, emailProvider]);

    const handleCloseCompose = () => {
        setIsComposeOpen(false);
        onComposeClose();
    }
    
    const isTokenValid = () => {
        return currentAccessToken && currentTokenExpiresAt && new Date() < currentTokenExpiresAt;
    };
    
    const updateConnection = (status, token, expiresAt, provider) => {
        if (typeof onConnect === 'function') {
            onConnect(status, token, expiresAt, provider);
        }
    };
    
    const getFreshAccessToken = async () => {
        try {
            if (isTokenValid()) {
                return currentAccessToken;
            }

            const userDoc = await getDoc(doc(db, "users", user.uid));
            if (!userDoc.exists()) {
                 throw new Error("User document not found.");
            }
            
            const data = userDoc.data();
            const provider = data.emailProvider;

            if (!provider) {
                throw new Error("Email provider not set for user.");
            }

            const tokenField = provider === 'microsoft' ? 'microsoftAccessToken' : 'gmailAccessToken';
            const expiryField = provider === 'microsoft' ? 'microsoftTokenExpiresAt' : 'gmailTokenExpiresAt';

            if (data[tokenField] && data[expiryField]) {
                const expiresAt = data[expiryField].toDate();
                if (new Date() < expiresAt) {
                    updateConnection(true, data[tokenField], expiresAt, provider);
                    return data[tokenField];
                }
            }
            // x6tokens-4b7e9c25 — X6 (#20): the access token's new home is private/mailSession (owner-only).
            if (provider === 'microsoft') {
                try {
                    const sessionSnap = await getDoc(doc(db, "users", user.uid, "private", "mailSession"));
                    const s = sessionSnap.exists() ? sessionSnap.data() : null;
                    const sExpires = s?.microsoftTokenExpiresAt?.toDate ? s.microsoftTokenExpiresAt.toDate() : null;
                    if (s?.microsoftAccessToken && sExpires && new Date() < sExpires) {
                        updateConnection(true, s.microsoftAccessToken, sExpires, provider);
                        return s.microsoftAccessToken;
                    }
                } catch (e) { /* refused or offline: ask the server below */ }
            }

            
            console.log(`Token expired or not found locally. Refreshing for provider: ${provider}`);
            const functionName = provider === 'microsoft' ? 'getMicrosoftAccessToken' : 'getAccessToken';
            const getAccessToken = httpsCallable(functions, functionName);
            const result = await getAccessToken();
            
            if (result.data && result.data.accessToken) {
                const token = result.data.accessToken;
                const newExpiresAt = result.data.expiresAt ? new Date(result.data.expiresAt) : new Date(Date.now() + 55 * 60 * 1000);
                updateConnection(true, token, newExpiresAt, provider);
                return token;
            } else {
                throw new Error("No access token returned from refresh function");
            }
        } catch (error) {
            console.error("Could not get fresh access token:", error);
            setError("Your session has expired. Please reconnect your email account.");
            updateConnection(false, null, null, null);
            return null;
        }
    };

    // googleoff-7d2e4a19 -- the Google connect redirect was removed; Microsoft is the only provider.

    // --- NEW: Server-Side Redirect Flow (Microsoft) ---
    //
    const handleConnectMicrosoft = () => {
        sessionStorage.setItem('pending_oauth', 'microsoft');
        const MICROSOFT_CLIENT_ID = "284ed566-9d31-431d-9366-3f9bbe11954d";
        const REDIRECT_URI = `${window.location.origin}/settings`;
        const scope = "User.Read Mail.Send Calendars.ReadWrite offline_access";
        
        const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
            `client_id=${MICROSOFT_CLIENT_ID}` +
            `&response_type=code` +
            `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
            `&response_mode=query` +
            `&scope=${encodeURIComponent(scope)}` +
            `&prompt=consent`;
            
        window.location.href = url;
    };
    
    const processGmailData = async (data, folderId, query, token) => {
        if (!data.messages) {
            if (query) setSearchResults([]);
            else setEmails(prev => ({ ...prev, [folderId]: [] }));
            setSelectedEmail(null);
            return;
        }
    
        const emailPromises = data.messages.map(async message => {
            try {
                const msgResponse = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${message.id}?format=full`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                
                if (!msgResponse.ok) return null;
                
                const msgData = await msgResponse.json();
                const headers = msgData.payload.headers;
                
                let body = 'No content available.';
                let isHtml = false;
                
                const findBody = (payload) => {
                    let htmlPart = null;
                    let textPart = null;
    
                    const findParts = (parts) => {
                        if (!parts) return;
                        for (const part of parts) {
                            if (part.mimeType === 'text/html') {
                                htmlPart = part.body.data;
                            } else if (part.mimeType === 'text/plain') {
                                textPart = part.body.data;
                            }
                            if (part.parts) {
                                findParts(part.parts);
                            }
                        }
                    };
    
                    if (payload.parts) {
                        findParts(payload.parts);
                    } 
                    else if (payload.body && payload.body.data) {
                        if (payload.mimeType === 'text/html') {
                            htmlPart = payload.body.data;
                        } else if (payload.mimeType === 'text/plain') {
                            textPart = payload.body.data;
                        }
                    }
    
                    if (htmlPart) {
                        isHtml = true;
                        return decodeBase64Url(htmlPart);
                    }
                    if (textPart) {
                        isHtml = false;
                        return decodeBase64Url(textPart);
                    }
                    return null;
                };
                
                const emailBody = findBody(msgData.payload);
                if (emailBody) {
                    body = emailBody;
                }
    
                // inboxxss-7c4f2a91 — the regex that stood here (strip <script>, <style>, and
                // QUOTED on*="…" handlers) was removed: it gave false confidence and missed
                // unquoted handlers, svg onload, javascript: links and forms. Both providers' HTML
                // is now sanitized in ONE place, EmailContentDisplay → utils/emailSanitizer.js.

                const snippet = decodeHtmlEntities(msgData.snippet || '');
                const subject = decodeHtmlEntities(headers.find(h => h.name.toLowerCase() === 'subject')?.value || '(No Subject)');
    
                return {
                    id: msgData.id,
                    from: headers.find(h => h.name.toLowerCase() === 'from')?.value || 'Unknown',
                    to: headers.find(h => h.name.toLowerCase() === 'to')?.value || 'Unknown',
                    date: headers.find(h => h.name.toLowerCase() === 'date')?.value || new Date().toISOString(),
                    subject: subject,
                    snippet: snippet,
                    body: body,
                    isHtml: isHtml,
                    isRead: !msgData.labelIds?.includes('UNREAD')
                };
            } catch (error) { 
                console.error('Error processing Gmail message:', error);
                return null; 
            }
        });
    
        const fetchedEmails = (await Promise.all(emailPromises)).filter(Boolean);
        
        if (query) setSearchResults(fetchedEmails);
        else setEmails(prev => ({ ...prev, [folderId]: fetchedEmails }));
    
        if (fetchedEmails.length > 0 && !selectedEmail) setSelectedEmail(fetchedEmails[0]);
    };
    
    const fetchGmailLabels = async (token) => {
        try {
            const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch Gmail labels: ${response.status}`);
            }
            
            const data = await response.json();
            if (data.labels) {
                let allLabels = data.labels;
                if (!allLabels.some(l => l.id === 'ALL')) {
                    allLabels.push({ id: 'ALL', name: 'All Mail', type: 'system' });
                }
                
                const sys = allLabels.filter(l => l.type === 'system' && l.id !== 'ALL');
                const usr = allLabels.filter(l => l.type === 'user');
                const allMail = allLabels.find(l => l.id === 'ALL');
                
                const sortedLabels = [...sys, allMail, ...usr].filter(Boolean);
                setLabels(sortedLabels);
                
                return sortedLabels;
            }
        } catch (error) {
            console.error('Error fetching Gmail labels:', error);
            throw error;
        }
    };

    const fetchGmailEmails = async (token, folderId, query = null) => {
        let url;
        if (query) url = `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}&maxResults=15`;
        else url = folderId === 'ALL' ? 'https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=15' : `https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=15&labelIds=${folderId}`;
        
        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) throw new Error(`Failed to fetch messages: ${response.status}`);
        const data = await response.json();
        await processGmailData(data, folderId, query, token);
    };

    const fetchMicrosoftFolders = async (token) => {
        try {
            const response = await fetch('https://graph.microsoft.com/v1.0/me/mailFolders?$select=id,displayName,parentFolderId', {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to fetch Microsoft folders: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
            }
            
            const data = await response.json();
            
            const transformedFolders = data.value.map(folder => ({
                id: folder.id === 'inbox' ? 'inbox' : folder.id,
                name: folder.displayName,
                type: folder.parentFolderId ? 'user' : 'system'
            }));
            
            const standardFolders = [
                { id: 'inbox', name: 'Inbox', type: 'system' },
                { id: 'sentitems', name: 'Sent Items', type: 'system' },
                { id: 'drafts', name: 'Drafts', type: 'system' },
                { id: 'deleteditems', name: 'Deleted Items', type: 'system' }
            ];
            
            const folderMap = new Map();
            
            standardFolders.forEach(folder => {
                folderMap.set(folder.id.toLowerCase(), folder);
            });
            
            transformedFolders.forEach(folder => {
                const key = folder.name.toLowerCase().replace(/\s+/g, '');
                if (key === 'inbox') {
                    folderMap.set('inbox', { ...folder, id: 'inbox' });
                } else if (key === 'sentitems' || key === 'sent') {
                    folderMap.set('sentitems', { ...folder, id: 'sentitems' });
                } else if (key === 'drafts') {
                    folderMap.set('drafts', { ...folder, id: 'drafts' });
                } else if (key === 'deleteditems' || key === 'deleted') {
                    folderMap.set('deleteditems', { ...folder, id: 'deleteditems' });
                } else {
                    folderMap.set(folder.id, folder);
                }
            });
            
            const allFolders = Array.from(folderMap.values());
            return allFolders;
        } catch (error) {
            console.error('Error fetching Microsoft folders:', error);
            throw error;
        }
    };

    const fetchMicrosoftEmails = async (token, folderId = 'inbox', query = null) => {
        try {
            const queryParams = `?$top=15&$select=id,subject,from,toRecipients,receivedDateTime,bodyPreview,body,isRead&$orderby=receivedDateTime desc`;
            let url;

            if (query) {
                url = `https://graph.microsoft.com/v1.0/me/messages?$search="${encodeURIComponent(query)}"` + queryParams.replace('?', '&');
            } else {
                const wellKnownFolders = ['inbox', 'sentitems', 'drafts', 'deleteditems'];
                if (wellKnownFolders.includes(folderId.toLowerCase())) {
                    const correctCaseFolderId = folderId.toLowerCase() === 'sentitems' ? 'sentItems' : folderId.toLowerCase();
                    url = `https://graph.microsoft.com/v1.0/me/mailFolders/${correctCaseFolderId}/messages` + queryParams;
                } else {
                    url = `https://graph.microsoft.com/v1.0/me/mailFolders/${folderId}/messages` + queryParams;
                }
            }
            
            const response = await fetch(url, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                
                if (response.status === 401) {
                    throw new Error("Authentication failed - token may be expired");
                }
                
                throw new Error(`Failed to fetch Microsoft emails: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
            }
            
            const data = await response.json();
            
            const transformedEmails = data.value.map(email => ({
                id: email.id,
                from: email.from?.emailAddress?.address || 'Unknown',
                to: email.toRecipients?.[0]?.emailAddress?.address || 'Unknown',
                date: email.receivedDateTime,
                subject: email.subject || '(No Subject)',
                snippet: email.bodyPreview || '',
                body: email.body?.content || 'No content available.',
                isHtml: email.body?.contentType === 'html',
                isRead: email.isRead !== false
            }));
            
            return transformedEmails;
        } catch (error) {
            console.error('Error fetching Microsoft emails:', error);
            throw error;
        }
    };

    // Mark an email as read in Gmail or Microsoft and update local state
    const markAsRead = async (email) => {
        if (email.isRead) return; // already read, skip API call
        const token = await getFreshAccessToken();
        if (!token) return;
        try {
            if (emailProvider === 'microsoft') {
                await fetch(`https://graph.microsoft.com/v1.0/me/messages/${email.id}`, {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isRead: true })
                });
            } else {
                await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${email.id}/modify`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ removeLabelIds: ['UNREAD'] })
                });
            }
            // Update local state so UI reflects read immediately without refetch
            const markRead = (list) => list.map(e => e.id === email.id ? { ...e, isRead: true } : e);
            setEmails(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(k => { updated[k] = markRead(updated[k]); });
                return updated;
            });
            if (searchResults) setSearchResults(prev => markRead(prev));
        } catch (err) {
            console.error('markAsRead error:', err);
        }
    };

    const fetchEmails = async (folderId, query = null) => {
        let token = await getFreshAccessToken();
        if (!token) return setIsLoading(false);
        setIsLoading(true);
        try {
            let fetchedEmails;
            if (emailProvider === 'microsoft') {
                fetchedEmails = await fetchMicrosoftEmails(token, folderId, query);
            } else {
                await fetchGmailEmails(token, folderId, query); 
                return; 
            }

            if (query) setSearchResults(fetchedEmails);
            else setEmails(prev => ({ ...prev, [folderId]: fetchedEmails }));
            if (fetchedEmails.length > 0 && !selectedEmail) setSelectedEmail(fetchedEmails[0]);
            setError('');
        } catch (err) {
            console.error(`Fetch Emails Error:`, err);
            setError('Error loading emails. Please try reconnecting.');
            updateConnection(false, null, null, null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            if (isConnected) {
                setIsLoading(true);
                try {
                    const token = await getFreshAccessToken();
                    if (token) {
                        let fetchedLabels;
                        if (emailProvider === 'microsoft') {
                            fetchedLabels = await fetchMicrosoftFolders(token);
                            setActiveFolder(f => f || 'inbox');
                        } else {
                            fetchedLabels = await fetchGmailLabels(token);
                            setActiveFolder(f => f || 'INBOX');
                        }
                        setLabels(fetchedLabels || []);
                    } else {
                        updateConnection(false, null, null, null);
                    }
                } catch (e) { console.error(e) } 
                finally { setIsLoading(false); }
            } else { setIsLoading(false); }
        };
        initialize();
    }, [isConnected, user, emailProvider]);

    useEffect(() => {
        if(isConnected && !searchResults && activeFolder){
            fetchEmails(activeFolder);
        }
    }, [activeFolder, isConnected, searchResults]);

    // Poll for new emails every 60 seconds
    useEffect(() => {
        if (!isConnected || !activeFolder) return;
        const interval = setInterval(() => {
            fetchEmails(activeFolder);
        }, 60000);
        return () => clearInterval(interval);
    }, [isConnected, activeFolder]);

    const sendGmailEmail = async (token, { to, cc, bcc, subject, body, attachments }) => {
        const boundary = "-------" + Date.now().toString(16);
        let emailBody = `Content-Type: multipart/mixed; boundary=${boundary}\r\n`;
        emailBody += `To: ${to}\r\n`;
        if (cc) emailBody += `Cc: ${cc}\r\n`;
        if (bcc) emailBody += `Bcc: ${bcc}\r\n`;
        emailBody += `Subject: ${subject}\r\n\r\n`;

        emailBody += `--${boundary}\r\n`;
        emailBody += `Content-Type: text/plain; charset="UTF-8"\r\n\r\n`;
        emailBody += `${body}\r\n\r\n`;

        attachments.forEach(file => {
            emailBody += `--${boundary}\r\n`;
            emailBody += `Content-Type: ${file.contentType}; name="${file.name}"\r\n`;
            emailBody += `Content-Disposition: attachment; filename="${file.name}"\r\n`;
            emailBody += `Content-Transfer-Encoding: base64\r\n\r\n`;
            emailBody += `${file.contentBytes}\r\n`;
        });

        emailBody += `--${boundary}--`;

        const base64EncodedEmail = btoa(unescape(encodeURIComponent(emailBody))).replace(/\+/g, '-').replace(/\//g, '_');
        const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
            method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ raw: base64EncodedEmail })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error.message || "Failed to send email.");
        }
    };

    const sendMicrosoftEmail = async (token, { to, cc, bcc, subject, body, attachments }) => {
        const toRecipients = to.split(',').filter(Boolean).map(email => ({ emailAddress: { address: email.trim() } }));
        const ccRecipients = cc.split(',').filter(Boolean).map(email => ({ emailAddress: { address: email.trim() } }));
        const bccRecipients = bcc.split(',').filter(Boolean).map(email => ({ emailAddress: { address: email.trim() } }));

        const payload = {
            message: {
                subject: subject,
                body: {
                    contentType: 'Text',
                    content: body
                },
                toRecipients: toRecipients,
                ccRecipients: ccRecipients,
                bccRecipients: bccRecipients,
                attachments: attachments.map(file => ({
                    '@odata.type': '#microsoft.graph.fileAttachment',
                    name: file.name,
                    contentType: file.contentType,
                    contentBytes: file.contentBytes
                }))
            },
            saveToSentItems: "true"
        };
        const response = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            console.error("Microsoft send email error:", errorData);
            throw new Error(errorData.error?.message || "Failed to send email.");
        }
    };
    
    const handleSendEmail = async ({ to, cc, bcc, subject, body, attachments }) => {
        const token = await getFreshAccessToken();
        if (!token) return setError("No access token available.");
        try {
            if (emailProvider === 'microsoft') {
                await sendMicrosoftEmail(token, { to, cc, bcc, subject, body, attachments });
            } else {
                await sendGmailEmail(token, { to, cc, bcc, subject, body, attachments });
            }
            
            const customer = customers.find(c => c.email && c.email.toLowerCase() === to.toLowerCase());
            if (customer) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                const orgId = userDoc.exists() ? userDoc.data().organizationId : null;
                if (orgId) {
                    await addDoc(collection(db, `organizations/${orgId}/customers/${customer.id}/notes`), {
                        contactDate: serverTimestamp(), 
                        contactType: 'Email', 
                        noteText: `Subject: ${subject}\n\n${body}`, 
                        status: customer.status,
                    });
                }
            }
            
            if (emailProvider === 'microsoft') {
                setActiveFolder('sentitems'); 
            } else {
                setActiveFolder('SENT');
            }
            
            setTimeout(() => {
                if (emailProvider === 'microsoft') {
                    fetchEmails('sentitems');
                } else {
                    fetchEmails('SENT');
                }
            }, 1000); 
            
        } catch (err) {
            console.error("Send email error:", err);
            setError(`Failed to send email. ${err.message}`);
        }
    };

    const handleGmailAction = async (token, action, messageId) => {
        let url, opts = { method: 'POST', headers: { Authorization: `Bearer ${token}` } };
        if (action === 'archive') {
            url = `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`;
            opts.headers['Content-Type'] = 'application/json';
            opts.body = JSON.stringify({ removeLabelIds: ['INBOX'] });
        } else if (action === 'delete') {
            url = `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/trash`;
        } else return;
        const r = await fetch(url, opts);
        if (!r.ok) throw new Error(await r.json().then(e => e.error?.message || `Failed to ${action}`));
    };

    const handleMicrosoftEmailAction = async (token, action, messageId) => {
        let destinationId;
        if (action === 'archive') destinationId = 'archive';
        else if (action === 'delete') destinationId = 'deleteditems';
        else return;
        const url = `https://graph.microsoft.com/v1.0/me/messages/${messageId}/move`;
        const opts = { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ destinationId }) };
        const r = await fetch(url, opts);
        if (!r.ok) throw new Error(await r.json().then(e => e.error?.message || `Failed to ${action}`));
    };
    
    const handleEmailAction = async (action, messageId) => {
        const token = await getFreshAccessToken();
        if (!token) return setError("No access token");
        try {
            if (emailProvider === 'microsoft') {
                await handleMicrosoftEmailAction(token, action, messageId);
            } else {
                await handleGmailAction(token, action, messageId);
            }
            setSelectedEmail(null);
            await fetchEmails(activeFolder);
        } catch (e) {
            console.error(e);
            setError(`Could not ${action}: ${e.message}`);
        }
    };

    const handleCreateGmailLabel = async (token, name) => {
        const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', {
            method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, labelListVisibility: 'labelShow', messageListVisibility: 'show' })
        });
        if (!response.ok) throw new Error(await response.json().then(e => e.error?.message || "Failed to create label."));
    };

    const handleCreateMicrosoftFolder = async (token, name) => {
        const response = await fetch('https://graph.microsoft.com/v1.0/me/mailFolders', {
            method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ displayName: name })
        });
        if (!response.ok) throw new Error(await response.json().then(e => e.error?.message || "Failed to create folder."));
    };

    const handleCreateLabel = async (name) => {
        const token = await getFreshAccessToken();
        if (!token) return;
        try {
            if (emailProvider === 'microsoft') {
                await handleCreateMicrosoftFolder(token, name);
                const folders = await fetchMicrosoftFolders(token);
                setLabels(folders);
            } else {
                await handleCreateGmailLabel(token, name);
                await fetchGmailLabels(token);
            }
            setShowCreate(false);
        } catch (e) {
            console.error(e);
            setError(`Could not create: ${e.message}`);
        }
    };
    
    const handleSearch = async (e) => {
        e.preventDefault();
        if(searchQuery.trim() === '') {
            setSearchResults(null);
            setActiveFolder(emailProvider === 'microsoft' ? 'inbox' : 'INBOX');
        } else {
            setSearchResults([]);
            await fetchEmails(activeFolder, searchQuery);
        }
    }

    const clearSearch = () => {
        setSearchResults(null);
        setSearchQuery('');
        setActiveFolder(emailProvider === 'microsoft' ? 'inbox' : 'INBOX');
    };

    const handleReply = () => {
        if (!selectedEmail) return;
        const subject = `Re: ${selectedEmail.subject}`;
        const body = `\n\n\n--- On ${new Date(selectedEmail.date).toLocaleString()}, ${selectedEmail.from} wrote: ---\n> ${selectedEmail.body.replace(/\n/g, '\n> ')}`;
        onComposeRequest(selectedEmail.from, subject, body);
    };

    const handleForward = () => {
        if (!selectedEmail) return;
        const subject = `Fwd: ${selectedEmail.subject}`;
        const body = `\n\n\n--- Forwarded message ---\nFrom: ${selectedEmail.from}\nDate: ${new Date(selectedEmail.date).toLocaleString()}\nSubject: ${selectedEmail.subject}\n\n${selectedEmail.body}`;
        onComposeRequest('', subject, body);
    };

    if (!isConnected && !isLoading) {
        return <EmailConnectScreen
            onConnectMicrosoft={handleConnectMicrosoft}
            error={error} 
        />;
    }
    
    const displayedEmails = searchResults !== null ? searchResults : (emails[activeFolder] || []);

    return (
        <>
            <CreateLabelModal isOpen={showCreate} onClose={() => setShowCreate(false)} onConfirm={handleCreateLabel} />
            <ComposeEmailModal isOpen={isComposeOpen} onClose={handleCloseCompose} handleSendEmail={handleSendEmail} initialTo={composeState.recipient} initialSubject={composeState.subject} initialBody={composeState.body} />
            <div className="flex h-[calc(100vh-150px)] bg-white">
                <EmailSidebar labels={labels} activeFolder={activeFolder} onSelectFolder={setActiveFolder} onCompose={() => onComposeRequest()} onCreateLabel={() => setShowCreate(true)} />
                <div className="w-1/3 border-r border-gray-200 overflow-y-auto flex flex-col">
                    <div className="p-2 border-b">
                        <form onSubmit={handleSearch} className="flex gap-2">
                           <div className="relative flex-grow">
                                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search mail" className="w-full pl-10 pr-4 py-2 border rounded-md" />
                           </div>
                           {searchResults !== null && <button type="button" onClick={clearSearch} className="px-3 py-2 bg-gray-200 rounded-md">Clear</button>}
                        </form>
                    </div>
                    {isLoading && <div className="p-4 text-center">Loading...</div>}
                    {!isLoading && displayedEmails.map(email => (
                        <div key={email.id} onClick={() => { setSelectedEmail(email); markAsRead(email); }} className={`p-4 border-b cursor-pointer ${selectedEmail?.id === email.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                            <div className={`flex items-center gap-2 ${email.isRead ? 'font-normal text-gray-700' : 'font-bold text-gray-900'}`}>
                                {!email.isRead && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                                <span className="truncate">{email.from}</span>
                            </div>
                            <div className={`text-sm ${email.isRead ? 'text-gray-600' : 'text-gray-900 font-semibold'}`}>{email.subject}</div>
                            <div className="text-xs text-gray-500 truncate">{email.snippet}</div>
                        </div>
                    ))}
                    {!isLoading && displayedEmails.length === 0 && <div className="p-4 text-center text-gray-500">No emails in this folder.</div>}
                </div>
                <div className="flex-1 p-6 overflow-y-auto">
                    {selectedEmail ? (
                        <>
                           <div className="pb-4 border-b mb-4">
                                <h2 className="text-2xl font-bold mb-2">{selectedEmail.subject}</h2>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="flex items-center text-sm text-gray-500"><span>From: {selectedEmail.from}</span></div>
                                         <div className="flex items-center text-sm text-gray-500 mt-1"><span>To: {selectedEmail.to}</span></div>
                                        <div className="text-xs text-gray-400 mt-2">{new Date(selectedEmail.date).toLocaleString()}</div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={handleReply} className="p-2 hover:bg-gray-100 rounded-md" title="Reply"><CornerUpLeft size={18}/></button>
                                        <button onClick={handleForward} className="p-2 hover:bg-gray-100 rounded-md" title="Forward"><CornerUpRight size={18}/></button>
                                        <button onClick={() => handleEmailAction('archive', selectedEmail.id)} className="p-2 hover:bg-gray-100 rounded-md" title="Archive"><Archive size={18}/></button>
                                        <button onClick={() => handleEmailAction('delete', selectedEmail.id)} className="p-2 hover:bg-gray-100 rounded-md text-red-600" title="Delete"><Trash2 size={18}/></button>
                                    </div>
                                </div>
                            </div>
                            <EmailContentDisplay email={selectedEmail} />
                        </>
                    ) : (
                        <div className="text-center text-gray-500 pt-20">Select an email to read</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default EmailView;